import React, { Suspense, useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, Html, useGLTF } from "@react-three/drei";
import useQuestionnaireStore from "../store/useQuestionnaireStore";
import Button from "../components/common/Button";
import * as THREE from "three";
import useWarningModal from "../hooks/useWarningModal.js";

const modelPath = "/models/human-figure.glb";

// --- Data for Joints, Markers, and Camera Targets ---
// NOTE: You must adjust the 'markerPosition' and camera 'position'/'target'
// coordinates to fit YOUR specific model's size and position.
const jointData = [
  {
    id: 1,
    name: "Shoulder",
    markerPosition: [-0.35, 0.5, 0],
    camera: {
      position: new THREE.Vector3(-1.5, 2.5, 1),
      target: new THREE.Vector3(0, 1.2, 0),
    },
  },
  {
    id: 2,
    name: "Elbow",
    markerPosition: [0.4, 0, 0],
    camera: {
      position: new THREE.Vector3(1.6, 1.0, 0.6),
      target: new THREE.Vector3(0.5, 0.9, 0),
    },
  },
  {
    id: 3,
    name: "Wrist",
    markerPosition: [-0.4, -0.3, 0],
    camera: {
      position: new THREE.Vector3(-1.8, 0.8, 1),
      target: new THREE.Vector3(0.6, 0.7, 0),
    },
  },
  {
    id: 4,
    name: "Hip",
    markerPosition: [0.2, -0.2, 0.1],
    camera: {
      position: new THREE.Vector3(0.6, 0.9, 1),
      target: new THREE.Vector3(0.2, 0.8, 0),
    },
  },
  {
    id: 5,
    name: "Knee",
    markerPosition: [-0.15, -0.9, 0.2],
    camera: {
      position: new THREE.Vector3(-0.3, 0.3, 1),
      target: new THREE.Vector3(0.3, 0, 0),
    },
  },
  {
    id: 6,
    name: "Ankle",
    markerPosition: [0.2, -1.4, 0.1],
    camera: {
      position: new THREE.Vector3(0.5, 0.2, 1),
      target: new THREE.Vector3(-0.1, -0.5, -1),
    },
  },
  {
    id: 7,
    name: "Back",
    markerPosition: [0, 0.2, -0.2],
    camera: {
      position: new THREE.Vector3(0, 0.8, -1),
      target: new THREE.Vector3(0, 1.4, 1),
    },
  },
  {
    id: 8,
    name: "Neck",
    markerPosition: [0, 0.65, -0.2],
    camera: {
      position: new THREE.Vector3(0, 1.4, -1),
      target: new THREE.Vector3(0, 1.5, 2),
    },
  },
];

const defaultCamera = {
  position: new THREE.Vector3(0, 1, 3.5),
  target: new THREE.Vector3(0, 0.8, 0),
};

const CameraManager = ({ focusOn }) => {
  const controlsRef = useRef();
  const [isUserInteracting, setIsUserInteracting] = useState(false);

  const focusTarget = useMemo(() => {
    const joint = jointData.find((j) => j.name === focusOn);
    return joint ? joint.camera : defaultCamera;
  }, [focusOn]);

  // When the focus target changes, allow the automatic animation to happen
  useEffect(() => {
    setIsUserInteracting(false);
  }, [focusOn]);

  // Listen for user interaction with the controls
  useEffect(() => {
    const controls = controlsRef.current;
    if (controls) {
      const onStart = () => setIsUserInteracting(true);
      // No need for onEnd, once user interacts, they have full control until a new joint is selected
      controls.addEventListener("start", onStart);
      return () => controls.removeEventListener("start", onStart);
    }
  }, [controlsRef]);

  useFrame((state) => {
    // If the user is controlling the camera, do not run the automatic animation
    if (isUserInteracting) return;

    // Smoothly move the camera to the target position (Lerp)
    state.camera.position.lerp(focusTarget.position, 0.05);

    // Smoothly move the controls' target
    if (controlsRef.current) {
      controlsRef.current.target.lerp(focusTarget.target, 0.05);
      controlsRef.current.update();
    }
  });

  return (
    <OrbitControls
      ref={controlsRef}
      enablePan={true}
      enableZoom={true}
      enableRotate={true}
      minDistance={1.5}
      maxDistance={8}
    />
  );
};

const InteractiveHumanModel = ({ onJointSelect, selectedJoint }) => {
  const { scene } = useGLTF(modelPath);
  const [hovered, setHovered] = useState(null);
  const [hoveredMarker, setHoveredMarker] = useState(null);

  // Memoize the scene traversal to prevent re-running on every render
  const interactiveScene = useMemo(() => {
    const sceneClone = scene.clone();
    sceneClone.traverse((child) => {
      if (child.isMesh && jointData.some((j) => j.name === child.name)) {
        const originalMaterial = child.material;
        child.material = originalMaterial.clone();

        // Update color based on state
        if (selectedJoint === child.name) {
          child.material.color.set("#007bff"); // Selected color
        } else if (hovered === child.name) {
          child.material.color.set("#5cacff"); // Hover color
        }

        // Add event listeners
        child.onPointerOver = (e) => {
          e.stopPropagation();
          setHovered(child.name);
        };
        child.onPointerOut = () => setHovered(null);
        child.onClick = (e) => {
          e.stopPropagation();
          onJointSelect(child.name);
        };
      }
    });
    return sceneClone;
  }, [scene, selectedJoint, hovered, onJointSelect]);

  return (
    <group position={[0, 1, 0]}>
      <primitive object={interactiveScene} scale={95} />
      {jointData.map((joint) => (
        <Html key={joint.id} position={joint.markerPosition}>
          <div
            className={`joint-marker ${
              selectedJoint === joint.name ? "selected" : ""
            }`}
            onClick={() => onJointSelect(joint.name)}
            onMouseEnter={() => setHoveredMarker(joint.id)}
            onMouseLeave={() => setHoveredMarker(null)}
            style={{ position: "relative" }}
          >
            {joint.id}
            {hoveredMarker === joint.id && (
              <div className="joint-tooltip">{joint.name}</div>
            )}
          </div>
        </Html>
      ))}
    </group>
  );
};

useGLTF.preload(modelPath);

const Step1_Location = () => {
  const navigate = useNavigate();
  const setPainLocation = useQuestionnaireStore(
    (state) => state.setPainLocation
  );
  const [selectedJoint, setSelectedJoint] = useState(null);

  const showWarningModal = useWarningModal();

  const handleSelect = (joint) => {
    setSelectedJoint((prev) => (prev === joint ? null : joint));
  };

  const handleNext = () => {
    if (selectedJoint) {
      setPainLocation(selectedJoint);
      console.log("Selected Pain Location:", selectedJoint);
      navigate("/step-2");
    } else {
      showWarningModal();
    }
  };

  return (
    <motion.div
      className="page step1-page"
      initial={{ opacity: 0, x: 100 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -100 }}
      transition={{ duration: 0.5 }}
    >
      <div className="step1-content">
        <h2>Where is your pain located?</h2>
        <p>Select a joint from the list or click a number on the model.</p>
      </div>

      <div className="step1-layout">
        <div className="left-panel">
          {jointData.map((joint) => (
            <button
              key={joint.id}
              className={`joint-button ${
                selectedJoint === joint.name ? "selected" : ""
              }`}
              onClick={() => handleSelect(joint.name)}
            >
              {joint.id}. {joint.name}
            </button>
          ))}
        </div>

        <div className="right-panel">
          <div className="canvas-container">
            <Canvas camera={{ position: defaultCamera.position, fov: 50 }}>
              <ambientLight intensity={1.5} />
              <directionalLight position={[10, 10, 5]} intensity={2} />
              <directionalLight position={[-10, 10, -5]} intensity={1} />
              <Suspense fallback={<Html center>Loading...</Html>}>
                <InteractiveHumanModel
                  onJointSelect={handleSelect}
                  selectedJoint={selectedJoint}
                />
              </Suspense>
              <CameraManager focusOn={selectedJoint} />
            </Canvas>
          </div>
        </div>
      </div>

      <div className="navigation-buttons">
        <Button onClick={handleNext}>Next</Button>
      </div>
    </motion.div>
  );
};

export default Step1_Location;
